// Drag-to-add (05h): main.js's side-panel drag, copied exactly. One engine for everything
// that adds to the canvas by dragging:
//   - a side-panel tile (with any `data-context-*` state it carries);
//   - a bare player token (a Squad / Rankings / Filter / Similarity / Zone Finder /
//     Comparison row, a comparison's legend chip). It makes a Bio on a free cell,
//     or switches / overlays / joins the module it lands on;
//   - Stats' rank icon (a Ranking seeded with that metric);
//   - a team token (a League Table row, ?live=1; teams and matches, Ben 2026-10-30). It makes a
//     Bio showing the team on a free cell; on a Bio it shows the team, on a League Table / Squad
//     / Match Results it opens that team's league, squad or results (team-modules.ts);
//   - a saved-dashboard card (its drop loads that dashboard).
// The ghost is a plain element on <body> and the outline is #snap-indicator, as main.js
// had them. The pure rules are menu-drag-rules.ts.
//
// ONE undo step per drop: the checkpoint is taken at the first real move, before the
// canvas opens. It's held through the drop, so the merge / switch helpers add no second
// one, and a drop that adds nothing takes it back off.
//
// The listeners are added when a drag starts and removed when it ends, like the move's
// (05h fork 1A). They run after the handle halo's own pointerup, so the end recomputes the
// halo itself.

import { setModState } from "../modules/bridge.js";
import type { TeamRef } from "../live/team.js";
import { applyTeamDrop, moduleTakesMatches, moduleTakesTeams, stickyTeamState } from "./team-modules.js";
import { setStickyTeam } from "./sticky-team.js";
import { joinTeam, statsTileShowsTeam, teamJoinsTile, teamRefusedBy } from "./team-merge.js";
import { completePanelDetach } from "./map-panels.js";
import type { MatchRef } from "../live/match.js";
import { dropMatchFocus, narrowMapToMatch, narrowsToMatch, takesMatchFocus } from "./match-drag.js";
import type { EventFocus } from "../live/event-focus.js";
import { readSnapshot } from "../data/snapshot.js";
import { LIVE } from "../live/flag.js";
import { followPlayerLeague, followsLeague } from "./league-follow.js";
import { playerById } from "../data/players.js";
import { pixelSize } from "./module-shell.js";
import { getDefaultSize } from "../modules/module-config.js";
import { STEP } from "./placement.js";
import {
  collapseWorkspace,
  collapseWorkspaceOnTarget,
  expandWorkspace,
  findOverlappingModule,
  getSnapCoords,
  isAreaClear,
  isDragExpanded,
} from "./canvas-space.js";
import {
  isMergeCompatibleTitle,
  isPlayerTokenTarget,
  isSimilarityOverlayTarget,
  isSwitchPlayerTarget,
  mergeModules,
  setModulePlayer,
  setSimilarityOverlayCandidate,
} from "./drop-actions.js";
import { autoScrollSpeed } from "./move-rules.js";
import { refreshResizeDominance } from "./resize-dominance.js";
import { canvasApi } from "./shell-actions.js";
import { getModuleConfigSync, templateModuleSize, whenModulesReady } from "../modules/module-config.js";
import { loadPreset } from "./presets.js";
import { getStickyPlayer } from "./sticky-player.js";
import { undoApi } from "./undo.js";
import { getScale as scale } from "./zoom.js";
import {
  PRESET_GHOST_COLS,
  PRESET_GHOST_ROWS,
  SIDE_RAIL_WIDTH,
  ghostSnapPoint,
  isMenuDragStart,
  menuDrop,
  readTemplateContext as readContextRule,
  type MenuDropKind,
} from "./menu-drag-rules.js";
import { hideTooltip } from "./tooltip.js";

type DragContext = Record<string, unknown>;

interface MenuDrag {
  /** A tile / the rank icon, a bare player, a team, a match, or a saved-dashboard card. */
  kind: "module" | "player" | "team" | "match" | "preset";
  /** The module a drop creates (a player makes a Bio). */
  title: string;
  baseCols: number;
  baseRows: number;
  /** `_`-state the new module is seeded with (a tile's data-context-*, a chip's season…). */
  context: DragContext | null;
  presetKey: string | null;
  /** The press point, page coordinates. */
  startX: number;
  startY: number;
  /** Past the threshold — a real drag. */
  moved: boolean;
  /** The first real move took the checkpoint (a cancelled drop takes it back off). */
  checkpointTaken: boolean;
  ghost: HTMLElement;
}

/** The pointer fields the ghost needs — a real event, or one made up from a scroll tick. */
interface PointerPoint {
  pageX: number;
  pageY: number;
  clientX: number;
  clientY: number;
}

let drag: MenuDrag | null = null;
// Kept between drags, as main.js kept them: a drag's first move compares its direction
// against where the last drag's pointer was (only the scroll's "moving away" guard reads it).
let lastClientX = 0;
let lastClientY = 0;
let speedX = 0;
let speedY = 0;
let scrollRaf: number | null = null;

function byId(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} missing from legacy-scaffold.html`);
  return el;
}

/** A side-panel tile / token / rank-icon / dashboard-card drag is on (the handle halo
 *  and the side panel's auto-close pause). */
export function isDraggingFromMenu(): boolean {
  return drag !== null;
}

/** A tile's `data-context-*` state (menu-drag-rules.ts), read off the element. */
export function readTemplateContext(template: Element): Record<string, string> | null {
  return readContextRule(Array.from(template.attributes));
}

function start(
  e: PointerPoint,
  kind: MenuDrag["kind"],
  title: string,
  cols: number,
  rows: number,
  context: DragContext | null,
  presetKey: string | null = null,
): MenuDrag {
  document.body.classList.add("is-dragging-module");
  hideTooltip();

  const ghost = document.createElement("div");
  ghost.className = "dragging-ghost";
  ghost.style.width = `${pixelSize(cols)}px`;
  ghost.style.height = `${pixelSize(rows)}px`;
  ghost.style.transformOrigin = "center center";
  // A little smaller while it's still a press; full size once it really moves.
  ghost.style.transform = `scale(${scale() * 0.92})`;
  ghost.style.opacity = "0.95";
  document.body.appendChild(ghost);

  const d: MenuDrag = {
    kind,
    title,
    baseCols: cols,
    baseRows: rows,
    context,
    presetKey,
    startX: e.pageX,
    startY: e.pageY,
    moved: false,
    checkpointTaken: false,
    ghost,
  };
  if (!drag) {
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
  }
  drag = d;
  return d;
}

/** Drag a NEW `title` module (a tile, the rank icon), seeded with `context` when dropped. */
export function startMenuDrag(e: PointerEvent, title: string, cols: number, rows: number, context?: DragContext | null): void {
  moveGhost(start(e, "module", title, cols, rows, context ?? null), e);
}

/** Drag a bare player (the sticky pick). `context` is a legend chip's own
 *  `{ _selectedSeason }`, so a merge shows the season the chip was on. Dropped on a free cell it
 *  makes a Bio at the side panel's size, so both ways of adding a Bio match (Ben,
 *  2026-10-19: one came out 3 × 4, the other 2 × 4). */
export function startPlayerTokenDrag(e: PointerEvent, context?: DragContext | null): void {
  const { cols, rows } = getDefaultSize("Bio");
  moveGhost(start(e, "player", "Bio", cols, rows, context ?? null), e);
}

/** Drag a team (?live=1). Dropped on a free cell it makes a Bio showing the team, at the side
 *  panel's Bio size; on a Bio it switches that Bio to the team. */
export function startTeamTokenDrag(e: PointerEvent, team: TeamRef): void {
  const { cols, rows } = getDefaultSize("Bio");
  moveGhost(start(e, "team", "Bio", cols, rows, { _team: team }), e);
}

/** Drag a match (?live=1): a Match module showing it on a free cell; on a Match it shows it there.
 *  A game from a player's Matches also carries the player (`focus`): onto a map it narrows that
 *  map as it always did (the Heat Map, Passing Profile and Report too). */
export function startMatchCardDrag(e: PointerEvent, match: MatchRef, focus?: EventFocus): void {
  const { cols, rows } = getDefaultSize("Match");
  moveGhost(start(e, "match", "Match", cols, rows, { _match: match, ...(focus ? { _matchFocus: focus } : {}) }), e);
}

/** How far (px) the pointer must move while pressed before a row's drag starts. */
const DRAG_START_PX = 5;

/**
 * Like `startPlayerTokenDrag`, but only once the pointer has moved a few pixels while pressed —
 * so a plain click on a row that is also clickable (Similarity) shows no drag ghost (Ben,
 * 2026-10-16). `onStart` runs just before the drag begins (e.g. noting the player).
 */
export function startPlayerTokenDragOnMove(
  e: PointerEvent,
  onStart?: () => void,
  context?: DragContext | null,
): void {
  const x0 = e.clientX;
  const y0 = e.clientY;
  const onMove = (ev: PointerEvent) => {
    if (Math.hypot(ev.clientX - x0, ev.clientY - y0) < DRAG_START_PX) return;
    done();
    onStart?.();
    startPlayerTokenDrag(ev, context);
  };
  const done = () => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", done);
    window.removeEventListener("pointercancel", done);
  };
  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", done);
  window.addEventListener("pointercancel", done);
}

/** Like `startTeamTokenDrag`, but only once the pointer has moved a few pixels while pressed, so
 *  a plain click on the row keeps doing what it did (a League Table row opens the squad). */
export function startTeamTokenDragOnMove(e: PointerEvent, team: TeamRef): void {
  const x0 = e.clientX;
  const y0 = e.clientY;
  const onMove = (ev: PointerEvent) => {
    if (Math.hypot(ev.clientX - x0, ev.clientY - y0) < DRAG_START_PX) return;
    done();
    startTeamTokenDrag(ev, team);
  };
  const done = () => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", done);
    window.removeEventListener("pointercancel", done);
  };
  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", done);
  window.addEventListener("pointercancel", done);
}

/** Like `startMatchCardDrag`, but only once the pointer has moved a few pixels while pressed, so
 *  a plain click (a team Matches' row hover, a result) does what it did. */
export function startMatchCardDragOnMove(e: PointerEvent, match: MatchRef, focus?: EventFocus): void {
  const x0 = e.clientX;
  const y0 = e.clientY;
  const onMove = (ev: PointerEvent) => {
    if (Math.hypot(ev.clientX - x0, ev.clientY - y0) < DRAG_START_PX) return;
    done();
    startMatchCardDrag(ev, match, focus);
  };
  const done = () => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", done);
    window.removeEventListener("pointercancel", done);
  };
  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", done);
  window.addEventListener("pointercancel", done);
}

/** Drag a saved-dashboard card: the ghost is a labelled placeholder; the drop loads it. */
function startPresetDrag(e: PointerEvent, key: string, label: string): void {
  const d = start(e, "preset", "Saved Notes", PRESET_GHOST_COLS, PRESET_GHOST_ROWS, null, key);
  moveGhost(d, e);
  d.ghost.classList.add("preset-drag-ghost");
  d.ghost.textContent = label;
}

/** The ghost follows the pointer; the outline shows where it lands. Blue = it combines
 *  with the module there (sized to THAT module), red = it can't go there. */
/** A player name dropped here replaces what it shows: a map; a Stats tile or Radar showing teams
 *  (never joins them as a player); a Bio, player or team (Ben, 2026-11-02: a Bio switched to Man
 *  City refused Jeremy Doku; rules.md "A player meets a team"). */
function takesPlayerSwitch(mod: HTMLElement | null): boolean {
  const title = mod?.querySelector(".module-title")?.textContent?.trim() ?? "";
  const isBio = LIVE && getModuleConfigSync(title)?.renderer === "renderBioModule";
  return isSwitchPlayerTarget(mod) || statsTileShowsTeam(mod) || isBio;
}

function moveGhost(d: MenuDrag, e: PointerPoint): void {
  const s = scale();
  const indicator = byId("snap-indicator");

  // A dashboard card never snaps: its drop replaces the whole canvas wherever it lands.
  if (d.kind === "preset") {
    d.ghost.style.left = `${e.clientX - (pixelSize(d.baseCols) * s) / 2}px`;
    d.ghost.style.top = `${e.clientY - (pixelSize(d.baseRows) * s) / 2}px`;
    indicator.style.display = "none";
    return;
  }

  // The dragged item's own size, fixed for the drag (the ghost's style is resized to a
  // merge target while hovering one, so it can't be read back).
  const baseW = pixelSize(d.baseCols);
  const baseH = pixelSize(d.baseRows);

  // Over the side rail: no outline.
  if (e.clientX <= SIDE_RAIL_WIDTH) {
    d.ghost.style.width = `${baseW}px`;
    d.ghost.style.height = `${baseH}px`;
    d.ghost.style.left = `${e.clientX - (baseW * s) / 2}px`;
    d.ghost.style.top = `${e.clientY - (baseH * s) / 2}px`;
    indicator.style.display = "none";
    return;
  }

  const point = ghostSnapPoint(e.pageX, e.pageY, s, baseW, baseH);
  const snap = getSnapCoords(point.x, point.y);

  let boxW = baseW;
  let boxH = baseH;
  let boxX = snap.x;
  let boxY = snap.y;
  let indicatorClass: "merge" | "invalid" | null = null;

  if (!isAreaClear(snap.col, snap.row, d.baseCols, d.baseRows, null, d.title === "Notes")) {
    // Taken — but a module it combines with is a merge waiting to happen, not a dead end.
    const overlapping = findOverlappingModule(snap.col, snap.row, d.baseCols, d.baseRows);
    const compatible =
      d.kind === "team"
        ? moduleTakesTeams(overlapping) && !teamRefusedBy(overlapping, d.context?._team as TeamRef)
        : d.kind === "match"
        ? moduleTakesMatches(overlapping) || narrowsToMatch(overlapping) || takesMatchFocus(overlapping, matchFocusOf(d))
        : // A Stats tile or Radar showing teams: a player replaces them (Ben, 2026-11-02, as a team
        // replaces a player); nothing else joins it.
        statsTileShowsTeam(overlapping)
        ? d.kind === "player"
        : d.kind === "player"
        ? // A chip's own season, so the duplicate check compares what this drop would add.
          isPlayerTokenTarget(overlapping, null, (d.context ? d.context._selectedSeason : null) as string | null | undefined) ||
          takesPlayerSwitch(overlapping) ||
          (LIVE && !!overlapping && followsLeague(overlapping)) ||
          (LIVE && moduleTakesMatches(overlapping)) ||
          isSimilarityOverlayTarget(overlapping)
        : !!overlapping && isMergeCompatibleTitle(d.title, overlapping);
    if (compatible && overlapping) {
      indicatorClass = "merge";
      boxW = Number.parseInt(overlapping.style.width, 10) || baseW;
      boxH = Number.parseInt(overlapping.style.height, 10) || baseH;
      boxX = Number.parseInt(overlapping.style.left, 10) || snap.x;
      boxY = Number.parseInt(overlapping.style.top, 10) || snap.y;
    } else {
      indicatorClass = "invalid";
    }
  }

  d.ghost.style.width = `${boxW}px`;
  d.ghost.style.height = `${boxH}px`;
  d.ghost.style.left = `${e.clientX - (boxW * s) / 2}px`;
  d.ghost.style.top = `${e.clientY - (boxH * s) / 2}px`;

  indicator.style.display = "block";
  indicator.style.width = `${boxW}px`;
  indicator.style.height = `${boxH}px`;
  indicator.style.left = `${boxX}px`;
  indicator.style.top = `${boxY}px`;
  if (indicatorClass === "merge") {
    indicator.classList.remove("invalid");
    indicator.classList.add("merge");
  } else if (indicatorClass === "invalid") {
    indicator.classList.remove("merge");
    indicator.classList.add("invalid");
  } else {
    indicator.classList.remove("invalid", "merge");
  }
}

/** Scroll while the pointer sits near a screen edge, the ghost following the page. */
function autoScrollTick(): void {
  const d = drag;
  if (!d) {
    scrollRaf = null;
    return;
  }
  if (speedX !== 0 || speedY !== 0) {
    window.scrollBy(speedX, speedY);
    moveGhost(d, {
      pageX: lastClientX + window.scrollX,
      pageY: lastClientY + window.scrollY,
      clientX: lastClientX,
      clientY: lastClientY,
    });
  }
  scrollRaf = requestAnimationFrame(autoScrollTick);
}

function onPointerMove(e: PointerEvent): void {
  const d = drag;
  if (!d) return;
  const movingLeft = e.clientX < lastClientX;
  const movingRight = e.clientX > lastClientX;
  const movingUp = e.clientY < lastClientY;
  const movingDown = e.clientY > lastClientY;
  lastClientX = e.clientX;
  lastClientY = e.clientY;
  // Only toward an edge the pointer is moving to (or resting at): moving back from the
  // right edge mustn't keep the right scroll going.
  speedX = autoScrollSpeed(e.clientX, window.innerWidth, movingLeft, movingRight);
  speedY = autoScrollSpeed(e.clientY, window.innerHeight, movingUp, movingDown);

  if (!d.moved && isMenuDragStart(e.pageX - d.startX, e.pageY - d.startY)) {
    d.moved = true;
    d.ghost.style.transform = `scale(${scale()})`;
    d.ghost.style.opacity = "0.95";
    byId("side-panel").classList.remove("open");
    if (!isDragExpanded()) {
      // BEFORE the canvas opens (it moves every module), so Undo gets the page as it was.
      undoApi().saveState();
      d.checkpointTaken = true;
      expandWorkspace();
      // The canvas opening scrolled the page: this event's pageX / pageY are stale.
      moveGhost(d, { pageX: e.clientX + window.scrollX, pageY: e.clientY + window.scrollY, clientX: e.clientX, clientY: e.clientY });
      if (scrollRaf === null) scrollRaf = requestAnimationFrame(autoScrollTick);
      return;
    }
  }
  moveGhost(d, e);
}

/** The player's game a Matches bar carries (null for a plain match). */
function matchFocusOf(d: MenuDrag): EventFocus | null {
  return (d.context?._matchFocus as EventFocus | undefined) ?? null;
}

function judder(mod: HTMLElement): void {
  mod.classList.add("snap-judder");
  mod.addEventListener("animationend", () => mod.classList.remove("snap-judder"), { once: true });
}

/** The drop's action — everything but a dashboard card and a cancel. */
function drop(kind: Exclude<MenuDropKind, "load-preset" | "cancel">, d: MenuDrag, target: HTMLElement | null, x: number, y: number): void {
  const { createModule } = canvasApi();
  const sticky = getStickyPlayer();
  switch (kind) {
    case "switch": {
      if (!target) throw new Error("menu drop: a switch without a target");
      // A season dragged in with her (the Bio's career strip, Ben 2026-10-19): its keys go on first,
      // so the module opens on that season + league, not her current one.
      // A map showing a match or a team takes the player too: their own map, the whole season (Ben,
      // 2026-11-01: "it should always do the same thing"; their part in one game is the list's
      // names or a game from their Matches).
      if (d.context) for (const [k, v] of Object.entries(d.context)) setModState(target, k, v);
      // A bare player has no picker of its own: the sticky pick is who's dragged.
      setModulePlayer(target, sticky);
      collapseWorkspaceOnTarget(); // the view stays on the target
      return;
    }
    case "overlay": {
      if (!target) throw new Error("menu drop: an overlay without a target");
      // Never Similarity's base player (that's search-box only) — the overlay.
      // Live: her current season is the real one (null), not her snapshot record's.
      const current = LIVE ? null : playerById(readSnapshot().players, sticky)?.currentSeason;
      const season = ((d.context && d.context._selectedSeason) as string | null | undefined) || current;
      setSimilarityOverlayCandidate(target, sticky, season);
      collapseWorkspaceOnTarget(); // the view stays on the target
      return;
    }
    case "player-match": {
      if (!target) throw new Error("menu drop: a player onto a Match without a target");
      if (sticky === null) throw new Error("menu drop: a player drag without a player");
      // The sticky pick is the one dragged; MatchModule finds their latest game.
      setModState(target, "_match", undefined);
      setModState(target, "_matchFor", { player: sticky });
      canvasApi().refreshModuleBody(target);
      collapseWorkspaceOnTarget(); // the view stays on the target
      return;
    }
    case "player-league": {
      if (!target) throw new Error("menu drop: a league follow without a target");
      // The way a selected league module follows a linked pick (league-follow.ts): the sticky
      // player is the one dragged, so it opens on their league and club (Ben, 2026-10-30).
      followPlayerLeague([target], canvasApi().refreshModuleBody);
      collapseWorkspaceOnTarget(); // the view stays on the target
      return;
    }
    case "match-map": {
      if (!target) throw new Error("menu drop: a match onto a map without a target");
      const match = d.context?._match as MatchRef | undefined;
      if (!match) throw new Error("menu drop: a match drag without its match");
      const focus = d.context?._matchFocus as EventFocus | undefined;
      if (focus) dropMatchFocus(target, focus);
      else narrowMapToMatch(target, match);
      collapseWorkspaceOnTarget(); // the view stays on the target
      return;
    }
    case "match-switch": {
      if (!target) throw new Error("menu drop: a match switch without a target");
      const match = d.context?._match as MatchRef | undefined;
      if (!match) throw new Error("menu drop: a match drag without its match");
      setModState(target, "_match", match);
      canvasApi().refreshModuleBody(target);
      collapseWorkspaceOnTarget(); // the view stays on the target
      return;
    }
    case "team-switch": {
      if (!target) throw new Error("menu drop: a team switch without a target");
      const team = d.context?._team as TeamRef | undefined;
      if (!team) throw new Error("menu drop: a team drag without its team");
      // A Stats tile or Radar showing a team takes it alongside (team-merge.ts); anywhere else, a
      // player included, the team replaces what's shown (Ben, 2026-11-02: "drop the box and
      // overwrite the content").
      if (teamJoinsTile(target, team)) joinTeam(target, team);
      else applyTeamDrop(target, team);
      collapseWorkspaceOnTarget(); // the view stays on the target
      return;
    }
    case "token-merge": {
      if (!target) throw new Error("menu drop: a merge without a target");
      // Always combines, never replaces: a same-type module for the dragged player (the
      // TARGET's title + size — a bare player has neither) joins the target.
      const title = target.querySelector(".module-title")?.textContent?.trim() ?? "";
      const cfg = getModuleConfigSync(title);
      if (cfg) {
        if (cfg.cols === undefined || cfg.rows === undefined) throw new Error(`modules.json: ${title} has no size`);
        // The drag's `_`-state goes on before the first render (05i-1; main.js rendered twice).
        const shadow = createModule({ x, y, cols: cfg.cols, rows: cfg.rows, title, custom: d.context });
        mergeModules(shadow, target);
      }
      collapseWorkspaceOnTarget(); // the view stays on the target
      return;
    }
    case "merge": {
      if (!target) throw new Error("menu drop: a merge without a target");
      // Created as normal (it opens on the sticky player), then absorbed straight away.
      const dropped = createModule({ x, y, cols: d.baseCols, rows: d.baseRows, title: d.title || "Metric Block", custom: d.context });
      mergeModules(dropped, target);
      collapseWorkspaceOnTarget(); // the view stays on the target
      return;
    }
    case "place": {
      // main.js seeded the tile's context after the judder, as a second render; one render
      // with it already on (05i-1) ends the same. A new tile opens on the sticky team when a team
      // was picked last (sticky-team.ts); a team dragged out becomes it.
      const title = d.title || "Metric Block";
      if (d.kind === "team" && d.context?._team) setStickyTeam(d.context._team as TeamRef);
      const fromTeam = d.kind === "module" && LIVE ? stickyTeamState(title) : null;
      const custom = fromTeam ? { ...fromTeam, ...(d.context ?? {}) } : d.context;
      const dropped = createModule({ x, y, cols: d.baseCols, rows: d.baseRows, title, custom });
      // A map's panel dragged out (map-panels.ts): its map lets go of it, in this same step.
      const map = completePanelDetach(dropped);
      if (map) canvasApi().refreshModuleBody(map);
      collapseWorkspace(x, y, false);
      judder(dropped);
      return;
    }
  }
}

function onPointerUp(e: PointerEvent): void {
  const d = drag;
  if (!d) return;
  drag = null;
  document.removeEventListener("pointermove", onPointerMove);
  document.removeEventListener("pointerup", onPointerUp);
  speedX = 0;
  speedY = 0;
  document.body.classList.remove("is-dragging-module");

  const indicator = byId("snap-indicator");
  const snapLeft = Number.parseInt(indicator.style.left, 10);
  const snapTop = Number.parseInt(indicator.style.top, 10);
  const overRail = e.clientX <= SIDE_RAIL_WIDTH;
  const invalid = indicator.classList.contains("invalid");
  const isMerge = indicator.classList.contains("merge");
  const { dropLastCheckpoint, setCheckpointsHeld } = undoApi();

  // Only a blue outline has a module to act on.
  const target =
    d.kind !== "preset" && d.moved && !overRail && !invalid && isMerge
      ? findOverlappingModule(Math.round(snapLeft / STEP), Math.round(snapTop / STEP), d.baseCols, d.baseRows)
      : null;
  const kind = menuDrop({
    kind: d.kind,
    presetKey: d.presetKey,
    moved: d.moved,
    overRail,
    invalid,
    target: target
      ? {
          isSwitch: takesPlayerSwitch(target),
          isOverlay: isSimilarityOverlayTarget(target),
          isLeague: LIVE && followsLeague(target),
          isMatch: LIVE && moduleTakesMatches(target),
          isMap: d.kind === "match" && (narrowsToMatch(target) || takesMatchFocus(target, matchFocusOf(d))),
        }
      : null,
  });

  if (kind === "load-preset") {
    if (!d.presetKey) throw new Error("menu drop: a dashboard card without its key");
    // The drag's own checkpoint (its first move) is the step — a second one here would be
    // a snapshot of the opened canvas, needing an extra Undo.
    void loadPreset(d.presetKey);
    collapseWorkspace(0, 0, true);
  } else if (kind === "cancel") {
    // Nothing was added, so the drag's checkpoint is taken back off (else Undo would seem
    // to do nothing).
    if (d.kind === "preset") {
      if (d.checkpointTaken) dropLastCheckpoint();
      collapseWorkspace(0, 0, true);
    } else {
      collapseWorkspace(0, 0, true);
      if (d.checkpointTaken) dropLastCheckpoint();
    }
  } else {
    // One step for the whole drop: the merge / switch helpers would otherwise checkpoint
    // again, with the temporary module they create already on the canvas.
    setCheckpointsHeld(true);
    try {
      drop(kind, d, target, snapLeft, snapTop);
    } finally {
      setCheckpointsHeld(false);
    }
  }

  d.ghost.remove();
  const lifted = d.kind === "preset" ? ".module-template.is-lifting, .saved-note-card.is-lifting" : ".module-template.is-lifting";
  document.querySelectorAll(lifted).forEach((t) => t.classList.remove("is-lifting"));
  indicator.style.display = "none";
  indicator.classList.remove("invalid", "merge");
  // The halo's own pointerup ran first and saw the drag still on.
  refreshResizeDominance();
}

/**
 * Wire the side panel's tiles + saved-dashboard cards (desktop; their phone taps went with
 * main.js's mobile JS, 05k-1). Installed where main.js loaded, where it wired them. The
 * module search moves these same elements around, so listeners added once stay put.
 */
export function installMenuDrag(): void {
  document.querySelectorAll<HTMLElement>(".module-template").forEach((template) => {
    template.addEventListener("pointerdown", async (e) => {
      if (window.innerWidth <= 768) return;
      await whenModulesReady();
      const size = templateModuleSize(template);
      startMenuDrag(e, template.getAttribute("data-title") ?? "", size.cols, size.rows, readTemplateContext(template));
      template.classList.add("is-lifting");
    });
  });
  document.querySelectorAll<HTMLElement>(".saved-note-card[data-preset]").forEach((card) => {
    card.addEventListener("pointerdown", async (e) => {
      if (window.innerWidth <= 768) return;
      await whenModulesReady();
      const key = card.getAttribute("data-preset");
      const label = card.querySelector(".saved-note-card-title")?.textContent?.trim() || "Saved Dashboard";
      if (key) {
        startPresetDrag(e, key, label);
        card.classList.add("is-lifting");
      }
    });
  });
}

